import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { format } from 'date-fns';
import dayjs from 'dayjs';

// 1. 아이콘은 @mui/icons-material 에서 가져와야 합니다. (중괄호 사용)
import { 
  ReportProblem as IssueIcon,
  ChatBubbleOutline as ChatIcon,
  Close as CloseIcon 
} from '@mui/icons-material';

// 2. CloseIcon은 @mui/material(컴포넌트)에 없으므로 삭제해야 합니다.
import {
  Box, 
  Card, 
  Typography, 
  Button, 
  Stack, 
  GlobalStyles,
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Popover,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
  // CloseIcon 제거됨
} from '@mui/material';

import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import * as carApi from '../../lib/api/car';
import CustomToolbar from '../../components/common/datagrid/CustomToolbar';
import CustomLoadingOverlay from '../../components/common/datagrid/CustomLoadingOverlay';
import { CarSearchBox } from '../../components/carmng/CarSearchBox';
import BreadcrumbsContainer from '../../containers/common/breadcurmbs/BreadcrumbsContainer';

const CarListContainer = () => {
  const loginUser = JSON.parse(localStorage.getItem('user') || '{}');
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [searchKeyword, setSearchKeyword] = useState('');
  const isMounted = useRef(true);
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const handleConfirm = async (rowData) => {
    try {
      await carApi.registerUsageLog({ logId: rowData.logId, confirm: 'Y' });
      enqueueSnackbar(`${rowData.carNumber} 차량 확인 완료되었습니다.`, { variant: 'success', autoHideDuration: 2000 });
      fetchListData();
    } catch (error) {
      enqueueSnackbar('확인 처리 중 오류가 발생했습니다.', { variant: 'error' });
    }
  };

const [anchorEl, setAnchorEl] = useState(null);
const [currentDetail, setCurrentDetail] = useState('');
const [popoverTitle, setPopoverTitle] = useState(''); // 제목 상태 추가

const handlePopoverOpen = (event, title, detail) => {
  setAnchorEl(event.currentTarget);
  setPopoverTitle(title); // 제목 설정
  setCurrentDetail(detail); // 내용 설정
};

const handlePopoverClose = () => {
  setAnchorEl(null);
};

  const columns = [
    { 
      field: 'no', 
      headerName: 'No', 
      width: 40, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => params.api.getRowsCount() - params.api.getAllRowIds().indexOf(params.id)
    },
    {
      field: 'startDate', headerName: '사용 시작일', width: 130,
      renderCell: (params) => <Typography sx={{ fontSize: '0.85rem', color: '#444', fontWeight: 500 }}>{params.value}</Typography>
    },
    {
      field: 'endDate', headerName: '사용 종료일', width: 130,
      renderCell: (params) => <Typography sx={{ fontSize: '0.85rem', color: '#444', fontWeight: 500 }}>{params.value}</Typography>
    },
    { field: 'dptNm', headerName: '부서', width: 110, align: 'right', headerAlign: 'center', valueFormatter: ({ value }) => value?.toLocaleString() },
    { field: 'usrNm', headerName: '성명', width: 80, align: 'right', headerAlign: 'center', valueFormatter: ({ value }) => value?.toLocaleString() },
    {
      field: 'carNumber', headerName: '차량번호', width: 80, align: 'center', headerAlign: 'center',
      renderCell: (params) => <Chip label={params.value} size="small" sx={{ bgcolor: '#E3F2FD', color: '#1976D2', fontWeight: 700, borderRadius: '4px' }} />
    },
    { field: 'oilFee', headerName: '주유비', width: 80, align: 'right', headerAlign: 'center', valueFormatter: ({ value }) => value?.toLocaleString() },
    { field: 'beforeMileage', headerName: '주행 전(km)', width: 90, align: 'right', headerAlign: 'center', valueFormatter: ({ value }) => value?.toLocaleString() },
    {
      field: 'afterMileage', headerName: '주행 후(km)', width: 90, align: 'right', headerAlign: 'center',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 500, color: !params.value ? '#F57C00' : 'inherit', fontSize: '0.85rem' }}>
          {params.value ? params.value.toLocaleString() : '미정산'}
        </Typography>
      )
    },
    {
      field: 'tripMileage', headerName: '주행 거리(km)', width: 80, align: 'right', headerAlign: 'center',
      renderCell: (params) => {
        const { beforeMileage, afterMileage } = params.row;
        if (!afterMileage) return <Typography sx={{ color: '#999', fontSize: '0.85rem' }}>-</Typography>;
        const diff = afterMileage - beforeMileage;
        return <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1976D2' }}>{diff > 0 ? diff.toLocaleString() : 0}</Typography>;
      }
    },
    {
      field: 'visitPartnerNm', headerName: '방문 거래처명', flex: 1, minWidth: 160, align: 'center', headerAlign: 'center',
      renderCell: (params) => <Typography sx={{ fontWeight: 500, color: '#1A2035', fontSize: '0.85rem' }}>{params.value}</Typography>
    },
    { field: 'commuteMileage', headerName: '출.퇴근용(km)', width: 90, align: 'right', headerAlign: 'center', valueFormatter: ({ value }) => value?.toLocaleString() },
    { field: 'businessMileage', headerName: '일반 업무용(km)', width: 90, align: 'right', headerAlign: 'center', valueFormatter: ({ value }) => value?.toLocaleString() },
    {
      field: 'carIssue', headerName: '차량이상', width: 80, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', color: params.value === 'Y' ? '#d32f2f' : '#444' }}>
            {params.value || 'N'}
          </Typography>
          {params.row.carIssueDetail && (
            <IssueIcon 
              sx={{ fontSize: 18, color: '#d32f2f', cursor: 'help' }} 
              onMouseEnter={(e) => handlePopoverOpen(e, '차량 이상 내역', params.row.carIssueDetail)}
              onMouseLeave={handlePopoverClose}
            />
          )}
        </Stack>
      )
    },
    {
      field: 'washCar', headerName: '세차', width: 30, align: 'center', headerAlign: 'center',
      renderCell: (params) => <Typography sx={{ fontSize: '0.85rem' }}>{params.value}</Typography>
    },
    {
      field: 'status', headerName: '운행 상태', width: 80, align: 'center', headerAlign: 'center',
      renderCell: (params) => {
        const isOngoing = !params.row.endDate;
        return (
          <Chip label={isOngoing ? '운행 중' : '사용 완료'} size="small"
            sx={{ bgcolor: isOngoing ? '#FFF4E5' : '#E8F5E9', color: isOngoing ? '#F57C00' : '#2E7D32', fontWeight: 800, fontSize: '0.7rem' }}
          />
        );
      }
    },
    {
      field: 'comment', headerName: '기타사항', width: 60, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {params.value && (
            <ChatIcon  // 비고용 아이콘 (필요시 InfoIcon 등으로 변경 가능)
              sx={{ fontSize: 18, color: '#1976d2', cursor: 'help' }} 
              onMouseEnter={(e) => handlePopoverOpen(e, '기타사항', params.value)}
              onMouseLeave={handlePopoverClose}
            />
          )}
        </Stack>
      )
    },
    {
      field: 'confirm', headerName: '확인', width: 100, align: 'center', headerAlign: 'center',
      renderCell: (params) => {
        if (!params.value) return null;
        const isNeedConfirm = params.value === 'S';
        return (
          <Button variant={isNeedConfirm ? "outlined" : "contained"} size="small" color={isNeedConfirm ? "warning" : "primary"}
            onClick={(e) => { e.stopPropagation(); if (isNeedConfirm) handleConfirm(params.row); }}
            sx={{ fontSize: '0.8rem', fontWeight: 700, borderRadius: 2, minWidth: '80px' }}>
            {isNeedConfirm ? '확인 필요' : '확인 완료'}
          </Button>
        );
      }
    }
  ];

  const formik = useFormik({
    initialValues: { carUuid: '', searchKeyword: '', searchWords: '', searchStrtDt: null, searchEndDt: null, searchStatus: '', searchStrtDt: dayjs().startOf('month'), searchEndDt: dayjs().endOf('month'),},  onSubmit: () => fetchListData(),
  });

  const fetchListData = useCallback(async () => {
    try {
      if (isMounted.current) setLoading(true);
      const params = {
        searchKeyword,
        ...(formik.values.searchStrtDt && { searchStrtDt: format(new Date(formik.values.searchStrtDt), 'yyyyMMdd') }),
        ...(formik.values.searchEndDt && { searchEndDt: format(new Date(formik.values.searchEndDt), 'yyyyMMdd') }),
        ...(localStorage.getItem('loginMode') === 'car_service' && { usrUuid: loginUser?.usrUuid })
      };
      const response = await carApi.getCarUsageList(params);
      if (isMounted.current) setRows(response.data?.list || []);
    } catch (err) {
      if (isMounted.current) enqueueSnackbar('데이터 로드 실패', { variant: 'error' });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [searchKeyword, formik.values, enqueueSnackbar, loginUser?.usrUuid]);

  useEffect(() => {
    isMounted.current = true;
    fetchListData();
    return () => { isMounted.current = false; };
  }, [fetchListData]);

  return (
    <>
      <BreadcrumbsContainer title="총무 관리 / 차량 운행 기록 / 리스트" icon="common" />
      <CarSearchBox formik={formik} setSearchKeyword={setSearchKeyword} />
      <div style={{ display: 'flex', height: 'calc(100vh - 240px)' }}>
        <div style={{ flexGrow: 1 }}>
          <DataGridPro
            getRowId={(row) => row.logId}
            apiRef={apiRef}
            columns={columns}
            rows={rows || []}
            loading={loading}
            components={{ Toolbar: CustomToolbar, LoadingOverlay: CustomLoadingOverlay }}
            componentsProps={{ toolbar: { fileName: '차량_운행_리스트' } }}
            pageSize={pageSize}
            onPageSizeChange={(newPage) => setPageSize(newPage)}
            pagination
            disableSelectionOnClick
            getRowClassName={(params) => !params.row.endDate ? 'past-unissued-row' : ''}
            getDetailPanelContent={({ row }) => (
              <Box sx={{ p: 2, bgcolor: '#FFF5F5', borderTop: '1px solid #FED7D7' }}>
                <Typography variant="subtitle2" sx={{ color: '#C53030', fontWeight: 700, mb: 0.5 }}>⚠️ 차량 이상 상세 내역</Typography>
                <Typography variant="body2" sx={{ color: '#2D3748' }}>{row.carIssueDetail || "등록된 상세 내역이 없습니다."}</Typography>
              </Box>
            )}
            getDetailPanelHeight={() => 'auto'} 
            sx={{
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC' },
              '& .MuiDataGrid-columnHeader': { whiteSpace: 'normal !important', lineHeight: '1.2 !important', display: 'flex', alignItems: 'center', justifyContent: 'center' },
              '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.85rem', textAlign: 'center', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'visible', whiteSpace: 'normal', textOverflow: 'clip' },
              '& .MuiDataGrid-columnHeaderTitleContainer': { display: 'flex', justifyContent: 'center' },
              '& .MuiDataGrid-row': { borderBottom: '1px solid #EDF2F7', '&:hover': { backgroundColor: '#F8FAFC' } },
              '& .MuiDataGrid-cell': { fontSize: '0.85rem', color: '#2D3748', display: 'flex', alignItems: 'center', px: 2, py: 1 },
              '& .MuiDataGrid-cell--textRight': { justifyContent: 'flex-end', fontFamily: "'Roboto Mono', 'Noto Sans CJK KR'", fontWeight: 500, color: '#1A202C' }
            }}
          />
        </div>
      </div>
      <Popover
        sx={{ pointerEvents: 'none' }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ 
          sx: { 
            p: 2,                  // 여백을 2로 늘림
            minWidth: 200, 
            maxWidth: 350,         // 250에서 350으로 확대
            borderRadius: '10px', 
            boxShadow: '0px 6px 16px rgba(0,0,0,0.12)', 
            bgcolor: '#fff',
            border: `1px solid ${popoverTitle.includes('이상') ? '#d32f2f' : '#1976d2'}`,
            mt: -1 
          } 
        }}
      >
        <Box>
          <Typography sx={{ 
            fontSize: '0.85rem',   // 제목 글자 크기 확대
            fontWeight: 900, 
            color: popoverTitle.includes('이상') ? '#d32f2f' : '#1976d2',
            mb: 1                  // 제목 아래 간격 확대
          }}>
            {popoverTitle}
          </Typography>
          <Typography sx={{ 
            fontSize: '0.9rem',    // 내용 글자 크기 확대 (0.8 -> 0.9)
            color: '#333', 
            lineHeight: 1.5,       // 줄 간격 최적화
            whiteSpace: 'pre-wrap' 
          }}>
            {currentDetail}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default CarListContainer;
